import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { ArrowDropUp, ArrowDropDown } from '@material-ui/icons';
import { TextField, InputAdornment, Box, IconButton, withStyles } from '@material-ui/core';
import { z } from 'zod';

const STEP = 1;
const STEP_DIRECTION = {
  UP: 'up',
  DOWN: 'down',
};
const SUPPORTED_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];

const styles = {
  iconButton: { borderRadius: '0px', height: '8px', width: '16px', overflow: 'hidden' },
  inputContainer: { paddingRight: '8px' },
  adornmentContainer: { display: 'flex', flexDirection: 'row', gap: '8px' },
  textContainer: { marginTop: '15px', marginRight: '15px', width: '125px' },
  error: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
    },
  },
};

const propTypes = {
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  expectationsForm: PropTypes.bool,
  allowsZero: PropTypes.bool,
  error: PropTypes.bool,
  labelValue: PropTypes.string,
  errors: PropTypes.object,
  keyValue: PropTypes.string,
};
const defaultProps = {
  disabled: false,
  max: null,
  min: null,
  expectationsForm: false,
  allowsZero: false,
  error: false,
  labelValue: '',
  errors: undefined,
  keyValue: undefined,
};

const sanitizeInputValue = value => {
  if (value === '-' || value === '.' || value === '') return '';

  return Number(value).toString();
};

function NumberInput({
  classes,
  disabled,
  value,
  onChange,
  min,
  max,
  expectationsForm,
  allowsZero,
  labelValue,
  error,
  errors,
  keyValue,
  ...rest
}) {
  const NUMERIC_REGEX = allowsZero ? /^(0|[1-9]\d*)$/ : /^[1-9]\d*$/;

  const inputSchema = z.union([z.string().regex(NUMERIC_REGEX), z.literal('')]);

  const [internalValue, setInternalValue] = useState(value);

  // Update internal value when the value prop changes
  useEffect(() => {
    if (String(value) !== sanitizeInputValue(internalValue)) {
      setInternalValue(value);
    }
  }, [value]);

  const handleInputChange = event => {
    const { value: newStringValue } = event.target;
    const { success: isValidNumber, data: newInternalValue } = inputSchema.safeParse(newStringValue);
    if (isValidNumber) {
      setInternalValue(newInternalValue);
      onChange(sanitizeInputValue(newInternalValue));
    } else {
      setInternalValue('');
    }
  };

  const handleInputPaste = event => {
    // Prevent pasting non-numeric values
    const clipboardData = event.clipboardData.getData('text/plain');
    if (!inputSchema.safeParse(clipboardData).success) {
      event.preventDefault();
    }
  };

  const handleStep = direction => {
    const { data } = z.coerce.number().safeParse(internalValue);
    const currentValue = data ?? min ?? 1;
    const isValidStep = {
      [STEP_DIRECTION.UP]: max !== null ? currentValue < max : true,
      [STEP_DIRECTION.DOWN]: min !== null ? currentValue > min : true,
    };
    const newValue = {
      [STEP_DIRECTION.UP]: currentValue + STEP,
      [STEP_DIRECTION.DOWN]: currentValue - STEP,
    };

    if (isValidStep[direction]) {
      setInternalValue(String(newValue[direction]));
      onChange(String(newValue[direction]));
    }
  };

  const handleInputKeyDown = event => {
    const { key } = event;

    if (SUPPORTED_KEYS.includes(key)) {
      event.preventDefault();
    }

    switch (key) {
      case 'ArrowUp':
        handleStep(STEP_DIRECTION.UP);
        break;
      case 'ArrowDown':
        handleStep(STEP_DIRECTION.DOWN);
        break;
      case 'PageUp':
        handleStep(STEP_DIRECTION.UP);
        break;
      case 'PageDown':
        handleStep(STEP_DIRECTION.DOWN);
        break;
      case 'Home':
        if (max) {
          setInternalValue(String(max));
          onChange(String(max));
        }
        break;
      case 'End':
        if (min) {
          setInternalValue(String(min));
          onChange(String(min));
        }
        break;
      default:
        break;
    }
  };

  return (
    <TextField
      autoComplete="off"
      disabled={disabled}
      inputMode="numeric"
      max={max ?? undefined}
      min={min ?? undefined}
      size="small"
      type="text"
      value={internalValue}
      variant="outlined"
      className={clsx(
        expectationsForm ? classes.textContainer : '',
        expectationsForm && error && Number.isInteger(value) && classes.error
      )}
      placeholder={expectationsForm ? '##' : ''}
      InputLabelProps={{
        shrink: true,
        style: {
          color: expectationsForm && error && Number.isInteger(value) ? 'red' : '#494949',
        },
      }}
      InputProps={{
        classes: { root: classes.inputContainer },
        endAdornment: (
          <InputAdornment position="end">
            <Box className={classes.adornmentContainer}>
              <Box display="flex" flexDirection="column">
                <IconButton
                  aria-label="Increase value"
                  className={classes.iconButton}
                  disabled={disabled}
                  size="small"
                  tabIndex="-1"
                  onClick={() => handleStep(STEP_DIRECTION.UP)}
                >
                  <ArrowDropUp />
                </IconButton>
                <IconButton
                  aria-label="Decrease value"
                  className={classes.iconButton}
                  disabled={disabled}
                  size="small"
                  tabIndex="-1"
                  onClick={() => handleStep(STEP_DIRECTION.DOWN)}
                >
                  <ArrowDropDown />
                </IconButton>
              </Box>
            </Box>
          </InputAdornment>
        ),
      }}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onPaste={handleInputPaste}
      {...rest}
    />
  );
}

NumberInput.propTypes = propTypes;
NumberInput.defaultProps = defaultProps;

export default withStyles(styles)(NumberInput);
